import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme } from '../../styles/ThemeProvider';
import { formatCacheValue } from '../../lib/formatCacheValue';
import { OptionRow } from './OptionRow';
import type { SiblingVariant } from '../../lib/variantOptions';

/**
 * Maps the catalog's *unnormalized* `product_variants.attributes` keys onto a
 * small set of display axes, and records whether each axis is price-relevant
 * (Q1 in the plan). The catalog has real key drift — `color` vs `colorway`,
 * `size` vs `shoe_size`/`case_size`, `storage` vs `capacity`, `memory` vs
 * `ram`, `chip` vs `processor`, plus spaced variants like `case color`. We
 * fold synonyms so two siblings keyed `color` and `colorway` group under one
 * "Colour" axis rather than reading as two unrelated facets.
 *
 * Price-relevance follows Q1: storage / memory / cpu are price axes;
 * `colorway` (footwear) is price-relevant but plain `color` is record-only;
 * size and material are record-only. Unknown keys render raw and record-only.
 */
const ATTRIBUTE_ALIASES: Record<string, { axis: string; priceRelevant: boolean }> = {
  storage: { axis: 'Storage', priceRelevant: true },
  capacity: { axis: 'Storage', priceRelevant: true },
  memory: { axis: 'Memory', priceRelevant: true },
  ram: { axis: 'Memory', priceRelevant: true },
  chip: { axis: 'Chip', priceRelevant: true },
  processor: { axis: 'Chip', priceRelevant: true },
  colorway: { axis: 'Colour', priceRelevant: true },
  color: { axis: 'Colour', priceRelevant: false },
  exterior_color: { axis: 'Colour', priceRelevant: false },
  case_color: { axis: 'Colour', priceRelevant: false },
  'case color': { axis: 'Colour', priceRelevant: false },
  band_color: { axis: 'Colour', priceRelevant: false },
  frame_color: { axis: 'Colour', priceRelevant: false },
  size: { axis: 'Size', priceRelevant: false },
  shoe_size: { axis: 'Size', priceRelevant: false },
  case_size: { axis: 'Size', priceRelevant: false },
  screen_size: { axis: 'Size', priceRelevant: false },
  material: { axis: 'Material', priceRelevant: false },
  case_material: { axis: 'Material', priceRelevant: false },
  'case material': { axis: 'Material', priceRelevant: false },
};

function titleCase(raw: string): string {
  return raw
    .replace(/[_\s]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Resolve a raw JSONB attribute key to its display axis + price-relevance. */
function normalizeAttributeKey(rawKey: string): { axis: string; priceRelevant: boolean } {
  return ATTRIBUTE_ALIASES[rawKey.toLowerCase()] ?? { axis: titleCase(rawKey), priceRelevant: false };
}

interface DisplayAttribute {
  axis: string;
  value: string;
  priceRelevant: boolean;
}

interface VariantPickerProps {
  showPrices?: boolean;
  variants: SiblingVariant[];
  onSelect: (variant: SiblingVariant) => void;
}

/**
 * Catalog-bound variant picker. Renders each sibling variant as an `OptionRow`
 * named by its *distinguishing* attributes (the axes whose value differs
 * across the set), with its headline price as a whole-unit Cache value. An
 * unpriced sibling (catalogued but no price — Q3) is a greyed row showing "—",
 * and the current variant is the selected row (2px border, a11y `selected`).
 *
 * Selection is structured-only: tapping a row fires `onSelect` with the
 * chosen `SiblingVariant`; the caller owns the write. With fewer than two
 * variants there is nothing to choose between, so the picker renders an empty
 * state rather than a dead single-option list (Q2 — callers also gate on ≥2).
 */
export function VariantPicker({ variants, onSelect, showPrices = true }: VariantPickerProps) {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          gap: theme.spacing.sm,
        },
        empty: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
        },
      }),
    [theme],
  );

  // Determine which axes are "distinguishing": those whose value differs
  // across the set, or that are present on some variants but not all. Only
  // these are worth showing — shared attributes are noise.
  const distinguishingAxes = useMemo(() => {
    const valuesByAxis = new Map<string, Set<string>>();
    const countByAxis = new Map<string, number>();
    for (const v of variants) {
      const seenAxes = new Set<string>();
      for (const [rawKey, rawVal] of Object.entries(v.attributes)) {
        const { axis } = normalizeAttributeKey(rawKey);
        if (!valuesByAxis.has(axis)) valuesByAxis.set(axis, new Set());
        valuesByAxis.get(axis)!.add(String(rawVal));
        seenAxes.add(axis);
      }
      for (const axis of seenAxes) {
        countByAxis.set(axis, (countByAxis.get(axis) ?? 0) + 1);
      }
    }
    const distinguishing = new Set<string>();
    for (const [axis, values] of valuesByAxis) {
      if (values.size > 1 || (countByAxis.get(axis) ?? 0) < variants.length) {
        distinguishing.add(axis);
      }
    }
    return distinguishing;
  }, [variants]);

  if (variants.length < 2) {
    return (
      <View style={styles.container}>
        <Text style={styles.empty} testID="variant-picker-empty">
          No other variants to choose from.
        </Text>
      </View>
    );
  }

  const displayAttributes = (variant: SiblingVariant): DisplayAttribute[] => {
    const attrs: DisplayAttribute[] = [];
    for (const [rawKey, rawVal] of Object.entries(variant.attributes)) {
      const { axis, priceRelevant } = normalizeAttributeKey(rawKey);
      if (distinguishingAxes.has(axis)) {
        attrs.push({ axis, value: String(rawVal), priceRelevant });
      }
    }
    return attrs;
  };

  return (
    <View style={styles.container}>
      {variants.map((variant) => {
        const attrs = displayAttributes(variant);
        const name = attrs.map((attr) => `${attr.axis}: ${attr.value}`).join(' · ');
        const relevantAxes = attrs.filter((attr) => attr.priceRelevant).map((attr) => attr.axis);
        const detail =
          showPrices && relevantAxes.length > 0
            ? `${relevantAxes.join(' · ')} affects price`
            : undefined;
        const priced = variant.price != null && Number.isFinite(variant.price.avg);
        return (
          <OptionRow
            key={variant.variantId}
            testID={`variant-option-${variant.variantId}`}
            name={name}
            detail={detail}
            value={
              showPrices && priced && variant.price
                ? formatCacheValue(variant.price.avg, variant.price.currency)
                : undefined
            }
            unknown={showPrices && !priced}
            selected={variant.isCurrent}
            onPress={() => onSelect(variant)}
          />
        );
      })}
    </View>
  );
}
